import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthUser {
  username: string;
  displayName?: string;
  role?: string;
}

interface SetSessionPayload {
  accessToken: string;
  refreshToken?: string;
  tokenType?: string;
  expiresIn?: number;
  user?: AuthUser;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  tokenType: string | null;
  expiresAt: number | null;
  user: AuthUser | null;
  /**
   * 本次登录的会话编号：每次登录（含同一账号登出后再登录）都换一个新的；刷新令牌沿用，不算新的登录。
   * 只在本页面区分“是不是同一次登录”，不发给服务端，也不是凭据。未登录为 null；旧版本留下的会话为 null，直到下次登录。
   */
  loginSessionId: string | null;
  /** 登录成功：写入新会话并换一个新的登录会话编号 */
  setSession: (payload: SetSessionPayload) => void;
  /** 刷新令牌成功：只换令牌与有效期，沿用当前登录会话编号 */
  refreshSession: (payload: SetSessionPayload) => void;
  clear: () => void;
}

/** 新的登录会话编号：只需在本页面内互不相同，不承担安全用途。 */
function newLoginSessionId(): string {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  return `${Date.now().toString(36)}-${random}`;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      tokenType: null,
      expiresAt: null,
      user: null,
      loginSessionId: null,
      setSession: ({ accessToken, refreshToken, tokenType, expiresIn, user }) =>
        set({
          accessToken,
          refreshToken: refreshToken ?? null,
          tokenType: tokenType ?? 'Bearer',
          expiresAt: expiresIn ? Date.now() + expiresIn * 1000 : null,
          user: user ?? null,
          loginSessionId: newLoginSessionId(),
        }),
      refreshSession: ({ accessToken, refreshToken, tokenType, expiresIn, user }) =>
        set({
          accessToken,
          refreshToken: refreshToken ?? null,
          tokenType: tokenType ?? 'Bearer',
          expiresAt: expiresIn ? Date.now() + expiresIn * 1000 : null,
          user: user ?? null,
        }),
      clear: () =>
        set({
          accessToken: null,
          refreshToken: null,
          tokenType: null,
          expiresAt: null,
          user: null,
          loginSessionId: null,
        }),
    }),
    { name: 'aiot-admin-auth' },
  ),
);

export const getAccessToken = () => useAuthStore.getState().accessToken;
export const getRefreshToken = () => useAuthStore.getState().refreshToken;
